import type { PropertyListSection } from '~/components/PropertyList';
import type { DisplayPagination } from '~/composables/useListing/pagination';
import type { Property as PropertyType } from '~/core/entities/property';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { PageName } from '~/presenters/PropertyFiltersPresenter';

import {
	filtersEventsChangeKey,
	useFilters
} from '~/composables/useListing/filter';
import {
	DEFAULT_CLIENT_PAGINATION,
	Pagination
} from '~/composables/useListing/pagination';
import { EventBusKeys } from '~/core/helpers/symbols';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';

import { Property } from './property';

interface UseManagerReturn {
	properties: ComputedRef<PropertyType[]>;
	filters: ComputedRef<PropertyFilters>;
	filteredCount: ComputedRef<number>;
	displayPagination: ComputedRef<DisplayPagination>;
	isFetching: ComputedRef<boolean>;
	isFetchingOnClient: ComputedRef<boolean>;
	isFetchingForTheFirstTime: ComputedRef<boolean>;
	isFetchingListing: ComputedRef<boolean>;
	hasProperties: ComputedRef<boolean>;
	hasError: ComputedRef<boolean>;
	hasLessThenTenProperties: ComputedRef<boolean>;
	nextPage: () => void;
	updateFiltersManager: (
		filters: Partial<PropertyFilters>,
		improveChanges?: boolean
	) => void;
	updateFiltersManagerDebounced: (
		filters: Partial<PropertyFilters>,
		improveChanges?: boolean
	) => void;
	clearFilters: () => void;
}

type PerPageOptions = {
	perPageFirstPage?: number;
	perPageNextPages?: number;
};

/**
 * Manages property listing functionality, including filters, pagination, and property data fetching.
 * @returns Functions and reactive data for managing the property listing.
 */
export function useManager(
	options: {
		page?: PageName;
		collection?: string;
		perPageOptions?: PerPageOptions;
		initialFilters?: ComputedRef<Partial<PropertyFilters>>;
		includeH3Clusters?: boolean;
		config?: {
			immediate?: boolean;
			shouldScrollToTopOnFiltersChange?: boolean;
			scrollElement?: Ref<InstanceType<typeof PropertyListSection> | null>;
		};
	} = {}
): UseManagerReturn {
	const {
		page = 'imoveis',
		collection,
		perPageOptions = {},
		initialFilters,
		includeH3Clusters = false,
		config = {
			immediate: true,
			shouldScrollToTopOnFiltersChange: true,
			scrollElement: null
		}
	} = options;

	const { user } = useUserSession();
	const route = useRoute();
	const router = useRouter();
	const viewMoreBus = useEventBus<string>(EventBusKeys.viewMoreClicked);
	const { cookie: favoritesCookie } = useFavoritesCookie();
	const getCookieData = useCookieData();

	const {
		filters,
		resetChanges,
		resetFilters,
		clearFilters,
		getCurrentChange,
		updateMultipleFilters,
		updateMultipleFiltersDebounced,
		updatePathFromFilters,
		getStringifiedFilters,
		updateFiltersFromNewPath,
		updateLastRequestChanges,
		setInitialFiltersFromPath,
		filtersHasChangedSinceLastRequest,
		updateFiltersFromLocationSlugFilters
	} = useFilters(page);

	const {
		clientPagination,
		displayPagination,
		updateCount,
		nextClientPage,
		nextDisplayPage,
		reset: resetPagination,
		updateTotalPages
	} = Pagination();

	const {
		properties,
		addProperties,
		needMoreProperties,
		reset: resetProperties,
		movePropertiesFromCached
	} = Property(
		clientPagination,
		perPageOptions.perPageFirstPage ?? DEFAULT_CLIENT_PAGINATION.perPage,
		perPageOptions.perPageNextPages ?? DEFAULT_CLIENT_PAGINATION.perPage
	);

	/**
	 * Initializes filters based on the current URL path if no changes have been made.
	 */
	function setInitialFilters() {
		if (
			filters.value.addressSlug &&
			!filters.value.poiLocationLat &&
			!filters.value.poiLocationLng
		) {
			return;
		}
		if (getCurrentChange() === 0) {
			setInitialFiltersFromPath(collection);
		}
	}

	const requestCounter = ref(0);
	// Tracks whether the latest first-page request failed, so the page can tell a
	// transient failure apart from genuine "zero results" and not emit noindex.
	// Must be useState so the SSR value is serialized and the client hydrates with
	// the same value, avoiding a head mismatch on shouldAddNoindex.
	const lastRequestFailed = useState<boolean>(
		`properties-listing-failed-${collection ?? page}`,
		() => false
	);
	const currentRequestUsingFilters = ref<
		PropertyFilters & { page?: number; perPage?: number }
	>({ ...filters.value, perPage: clientPagination.value.perPage });
	// Dynamic cache key that includes favorites cookie to prevent stale disk cache
	const cacheKey = computed(() => `properties-listing-${collection ?? page}`);

	const propertiesDataFetcher = useAsyncData<null>(
		cacheKey.value,
		async () => {
			requestCounter.value++;
			const requestCount = requestCounter.value;

			try {
				setInitialFilters();

				if (filtersHasChangedSinceLastRequest()) {
					resetPagination();
					resetProperties();
				}

				const selectedFilters =
					PropertyFiltersPresenter.prepareFiltersForRequest(filters.value, {
						includeH3Clusters
					});

				currentRequestUsingFilters.value = {
					...initialFilters?.value,
					...selectedFilters
				};
				currentRequestUsingFilters.value = {
					...currentRequestUsingFilters.value,
					...(!currentRequestUsingFilters.value['countPluscodes']
						? {
								page: clientPagination.value.page,
								perPage: clientPagination.value.perPage
							}
						: {})
				};

				const [result, error] = await nuxtPropertiesRepository.list(
					{
						...currentRequestUsingFilters.value,
						markUserWishlistsId: user.value?.id
					},
					getCookieData()
				);

				if (error) {
					console.error(error);
					if (requestCount === requestCounter.value) {
						lastRequestFailed.value = true;
					}
					return null;
				}

				// Only update the count if the request is the last one
				if (requestCount === requestCounter.value) {
					lastRequestFailed.value = false;
					updateCount(result.pagination.filteredCount);
					updateTotalPages(result.pagination.totalPages);
					addProperties(result.properties);
					updateFiltersFromLocationSlugFilters(result.locationSlugFilters);
					updateLastRequestChanges();
				}

				return null;
			} catch (error) {
				if (requestCount === requestCounter.value) {
					lastRequestFailed.value = true;
				}
				return null;
			}
		},
		{
			server: true,
			immediate: import.meta.server,
			watch: page === 'colecoes' ? [favoritesCookie] : []
		}
	);

	/*
	 * Computed property returns a boolean value indicating whether the properties are being fetched.
	 */
	const isFetching = computed<boolean>(
		() => propertiesDataFetcher.status.value === 'pending'
	);

	const isFetchingListing = computed<boolean>(() => {
		return (
			propertiesDataFetcher.status.value === 'pending' &&
			!('countPluscodes' in currentRequestUsingFilters.value)
		);
	});

	/*
	 * Computed property returns a boolean value indicating whether the properties are being fetched in client side.
	 */
	const isFetchingOnClient = computed<boolean>(
		() => import.meta.client && propertiesDataFetcher.status.value === 'pending'
	);

	/**
	 * Checks if data is being fetched for the first page.
	 * @returns True if fetching for the first page, otherwise false.
	 */
	const isFetchingForTheFirstTime = computed<boolean>(() => {
		return (
			isFetching.value &&
			clientPagination.value.page === 1 &&
			properties.value.length === 0
		);
	});

	/**
	 * Computed property returns a boolean value indicating whether the properties list is empty.
	 */
	const hasProperties = computed<boolean>(() => properties.value.length > 0);
	const hasError = computed<boolean>(() => lastRequestFailed.value);
	/*
	 * Computed property returns a boolean value indicating whether the properties list has less than ten (10) properties.
	 */
	const hasLessThenTenProperties = computed<boolean>(
		() => properties.value.length < 10
	);
	/**
	 * Computed property returns the number of properties that have been filtered in request.
	 */
	const filteredCount = computed<number>(
		() => clientPagination.value.filteredCount
	);

	const lastRequestFilters = ref<PropertyFilters>({ ...filters.value });

	function syncPageQueryParamWithDisplayPagination() {
		router.replace({
			path: route.path,
			query: {
				...route.query,
				page: displayPagination.value.page,
				sortBy: filters.value.sortBy,
				orderBy: filters.value.orderBy
			}
		});
	}

	/**
	 * Fetches the next page of properties
	 * If more properties are needed, fetches from the server
	 * Otherwise, fetches from the client
	 */
	function nextPage() {
		if (needMoreProperties()) {
			nextClientPage();
			propertiesDataFetcher.execute();
		} else {
			nextClientPage();
			movePropertiesFromCached();
		}

		nextDisplayPage();
		syncPageQueryParamWithDisplayPagination();

		viewMoreBus.emit(page);
	}

	const filterEvents = useEventBus<string>(filtersEventsChangeKey);
	/**
	 * Handles filter changes and updates the URL path accordingly.
	 * Fetches new property data after updating filters.
	 */
	const filterEventsCleanUp = filterEvents.on(async () => {
		// When on collections page, preserve the current route slug (e.g., 'minha-colecao')
		// instead of overwriting it with an internal wishlist ID.
		let collectionForPath = collection;

		if (page === 'colecoes') {
			// Check if we're on a shared collection page
			if (route.path.includes('/compartilhada/')) {
				// For shared collections, keep the collection ID (not prefixed)
				collectionForPath =
					(route.params as Record<string, string | undefined>)?.wishlistSlug ||
					collection;
			} else {
				// For other collections (like 'minha-colecao'), use the route param or fallback
				collectionForPath =
					((route.params as Record<string, string | undefined>)
						?.wishlistSlug as string | undefined) || collection;
			}
		}

		updatePathFromFilters(collectionForPath);

		if (import.meta.client && config.shouldScrollToTopOnFiltersChange) {
			await config.scrollElement?.value?.scrollToTop();
		}

		await propertiesDataFetcher.execute();
	});

	onUnmounted(() => {
		filterEventsCleanUp();
	});

	/**
	 * Resets the states when navigating from imoveis to colecao or from colecao to imoveis
	 * Or moving from imoveis/colecao to another page that is not imoveis/colecao
	 */
	router.afterEach((to, from) => {
		const isNavigatingBetweenImoveis =
			from.fullPath.includes('imoveis') && to.fullPath.includes('colecao');
		const isNavigatingBetweenColecao =
			from.fullPath.includes('colecao') && to.fullPath.includes('imoveis');

		const isNavigatingBetweenAllowedPages =
			isNavigatingBetweenImoveis || isNavigatingBetweenColecao;

		if (isNavigatingBetweenAllowedPages) {
			resetPagination();
			resetChanges();
			resetFilters();
		}
	});

	function isSortingOnlyFilterChange(newFilters: PropertyFilters) {
		const hasSortingParametersChanged =
			newFilters.sortBy !== filters.value.sortBy ||
			newFilters.orderBy !== filters.value.orderBy;

		if (!hasSortingParametersChanged) return false;

		const currentFiltersExcludingSorting = { ...filters.value };
		const newFiltersExcludingSorting = { ...newFilters };

		delete currentFiltersExcludingSorting.sortBy;
		delete currentFiltersExcludingSorting.orderBy;
		delete newFiltersExcludingSorting.sortBy;
		delete newFiltersExcludingSorting.orderBy;

		return (
			getStringifiedFilters(currentFiltersExcludingSorting) ===
			getStringifiedFilters(newFiltersExcludingSorting)
		);
	}

	/**
	 * Watches route changes and updates filters, properties, and pagination when coming from another page.
	 */
	const nuxtApp = useNuxtApp();
	let hasSkippedHydration = false;
	watch(
		route,
		async newRoute => {
			// Skip only during actual SSR hydration (first page load from server)
			if (import.meta.client && nuxtApp.isHydrating && !hasSkippedHydration) {
				hasSkippedHydration = true;
				return;
			}

			let formattedPath = newRoute.fullPath;

			if (page === 'colecoes' && collection) {
				formattedPath = formattedPath.replace('minha-colecao', collection);
			}

			const newFilters = PropertyFiltersPresenter.getFiltersFromPath(
				formattedPath,
				{ ...filters.value, wishlistId: collection },
				page
			);

			const currentFilterState = getStringifiedFilters(
				lastRequestFilters.value
			);
			const newFilterState = getStringifiedFilters(newFilters);

			/**
			 * Determines whether the user navigated to the current page from a different page or
			 * directly landed on the page due to a route update (e.g., clicking a link within the listing).
			 * This variable represents the following flows:
			 * 1. Entry from another page.
			 * 2. Direct entry to the page but triggered by a route update (e.g., clicking a link within the listing).
			 */
			const hasFilterStateChanged =
				currentFilterState !== newFilterState ||
				(isFetchingForTheFirstTime.value && properties.value.length === 0);
			const isSortingOnlyChange = isSortingOnlyFilterChange(newFilters);
			const isOnlyPageChange =
				Object.keys(route.query).filter(
					key => key !== 'sortBy' && key !== 'orderBy'
				).length === 1 && 'page' in route.query;

			switch (true) {
				case hasFilterStateChanged && !isSortingOnlyChange:
					resetPagination();
					resetProperties();
					resetChanges();
					updateFiltersFromNewPath(route.fullPath);
					await propertiesDataFetcher.execute();

					if (import.meta.client && config.shouldScrollToTopOnFiltersChange) {
						await config.scrollElement?.value?.scrollToTop();
					}

					break;

				case isOnlyPageChange:
					// Keep current filters and sorting
					if (newFilters.sortBy && newFilters.orderBy) {
						updateMultipleFilters(
							{
								sortBy: newFilters.sortBy,
								orderBy: newFilters.orderBy
							},
							false
						);
						await propertiesDataFetcher.execute();
					}
					break;

				case isSortingOnlyChange:
					updateMultipleFilters(
						{
							sortBy: newFilters.sortBy,
							orderBy: newFilters.orderBy,
							wishlistId: newFilters.wishlistId
						},
						true
					);
					await propertiesDataFetcher.execute();
					break;
				default:
					if (import.meta.client) {
						await propertiesDataFetcher.execute();
					}
					break;
			}
			lastRequestFilters.value = { ...newFilters };
		},
		{ immediate: true }
	);

	// Retry once on the client after hydration if the SSR first request failed,
	// so users aren't stranded on an empty page. Gate on isHydrating so it only
	// runs for the SSR load (client navigations already fetch via the route watch).
	const wasHydratingOnSetup = nuxtApp.isHydrating;
	onMounted(() => {
		if (wasHydratingOnSetup && lastRequestFailed.value) {
			propertiesDataFetcher.execute();
		}
	});

	return {
		properties,
		filters,
		filteredCount,
		displayPagination,
		isFetching,
		isFetchingOnClient,
		isFetchingForTheFirstTime,
		isFetchingListing,
		hasProperties,
		hasError,
		hasLessThenTenProperties,
		nextPage,
		updateFiltersManager: updateMultipleFilters,
		updateFiltersManagerDebounced: updateMultipleFiltersDebounced,
		clearFilters
	};
}
